import { sprite } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { gem, R, ramp, type Ramp } from './craft';

/**
 * Hand-made goods pictures (kind `good`) kept with the item set: the seven breakthrough pills
 * (ยาทะลวงขั้น, bt-pill-0…6), knockout powder, calm incense, mantou and mind tea. The pills share
 * one open box; tier shows first in the box itself (its lacquer, its rim metal, corner caps from
 * tier 3, upswept horns from tier 5, so tiers part at 16 px), then in the pill's colour and the
 * qi rising off it.
 */

const porcelain = ramp('#e4e8ee');
const celadon = ramp('#8ac0a8');
const dough = ramp('#f0e4cc');
const smoke = ramp('#c8c0d8');

/** Pill colour per breakthrough tier, earth to heaven. */
const PILL: readonly Ramp[] = [
  ramp('#b07a4a'),
  ramp('#4ab07a'),
  ramp('#c8783a'),
  ramp('#b8c4d8'),
  ramp('#f0c040'),
  ramp('#a070e0'),
  ramp('#f4f0e0'),
];

/** The box per tier: its lacquer and its rim metal. */
const BOX: readonly { body: Ramp; rim: Ramp }[] = [
  { body: ramp('#8a6a48'), rim: ramp('#5a4a3a') },
  { body: ramp('#3a7a52'), rim: ramp('#8a6a48') },
  { body: ramp('#8a2a34'), rim: R.bronze },
  { body: ramp('#2a3a7a'), rim: ramp('#c8d0dc') },
  { body: ramp('#2a2430'), rim: R.gold },
  { body: ramp('#5a2a8a'), rim: R.gold },
  { body: ramp('#e8ece4'), rim: R.gold },
];

/**
 * An open box seen from the front, its lid tipped back, a pill on silk inside. The box carries the
 * tier: plain wood, green, brocade with a bronze rim, blue with silver caps, black with gold caps,
 * violet with gold horns, white jade with gold horns and a halo.
 */
function pillBox(p: Pix, tier: number): void {
  const pill = PILL[tier] ?? PILL[0]!;
  const { body, rim } = BOX[tier] ?? BOX[0]!;
  // Lid, open behind.
  p.poly(
    [
      [6, 13],
      [9, 7],
      [25, 7],
      [27, 13],
    ],
    body.d1,
  );
  p.line(9, 7, 25, 7, tier >= 2 ? rim.l1 : body.l1);
  // Box.
  p.rect(5, 17, 23, 10, body.b);
  p.rect(5, 26, 23, 2, body.d2);
  p.rect(5, 17, 4, 9, body.d1);
  if (tier === 2) for (let x = 7; x < 27; x += 4) p.px(x, 21, body.l1).px(x + 2, 23, body.d1);
  if (tier === 0) for (const y of [20, 23]) p.line(9, y, 26, y, body.d1);
  if (tier === 1) for (let x = 11; x < 27; x += 5) p.line(x, 18, x, 25, body.l1);
  // Rim along the box mouth (from tier 1), the metal of the tier.
  if (tier >= 1) {
    p.rect(4, 16, 25, 2, rim.b);
    p.line(4, 16, 28, 16, rim.l2);
    p.rect(4, 27, 25, 1, rim.d1);
  }
  // Corner caps (tier 3+), horns (tier 5+).
  if (tier >= 3)
    for (const x of [4, 25]) {
      p.rect(x, 16, 4, 4, rim.b);
      p.rect(x, 24, 4, 4, rim.d1);
      p.px(x + 3, 16, rim.l2);
    }
  if (tier >= 5) {
    p.path(
      [
        [4, 16],
        [2, 13],
        [3, 11],
      ],
      rim.b,
      2,
    );
    p.path(
      [
        [28, 16],
        [30, 13],
        [29, 11],
      ],
      rim.l1,
      2,
    );
  }
  // Silk lining and the pill.
  p.rect(6, 13, 21, 3, '#d8c098');
  p.rect(6, 15, 21, 1, '#a8885a');
  gem(p, 16, 12, 5, pill);
  if (tier >= 3) p.ring(16, 12, 6.5, 6.5, pill.l1, -2.6, -0.5);
  // Clasp: iron on the plain boxes, then the rim metal.
  p.rect(15, 19, 3, 3, tier >= 2 ? rim.b : R.iron.b);
  p.px(17, 19, tier >= 2 ? rim.l2 : R.iron.l1);
  // Qi motes rising: one per tier step above the first, the top tiers ringed.
  const motes: [number, number][] = [
    [11, 6],
    [21, 5],
    [16, 3],
    [8, 9],
    [25, 9],
    [13, 2],
  ];
  for (let k = 0; k < tier; k++) {
    const [x, y] = motes[k]!;
    p.px(x, y, pill.l2);
    if (tier >= 4) p.px(x, y + 1, pill.b);
  }
  if (tier >= 5) p.ring(16, 12, 8.5, 7.5, pill.l2, -2.9, -0.2);
}

/** The same box drawn at 16 by hand: box colour, rim, caps and horns kept, the motes cut to a few. */
function pillBox16(p: Pix, tier: number): void {
  const pill = PILL[tier] ?? PILL[0]!;
  const { body, rim } = BOX[tier] ?? BOX[0]!;
  p.rect(4, 4, 8, 3, body.d1);
  p.line(4, 4, 11, 4, tier >= 2 ? rim.l1 : body.l1);
  p.rect(2, 9, 12, 5, body.b);
  p.rect(2, 9, 2, 5, body.d1);
  p.rect(2, 13, 12, 1, body.d2);
  if (tier >= 1) {
    p.rect(2, 8, 12, 1, rim.l1);
    if (tier >= 3) p.rect(2, 8, 2, 2, rim.b).rect(12, 8, 2, 2, rim.b);
  }
  if (tier >= 5) p.px(1, 7, rim.b).px(1, 6, rim.l1).px(14, 7, rim.l1).px(14, 6, rim.l2);
  p.rect(3, 7, 10, 1, '#d8c098');
  gem(p, 8, 6, 2.6, pill);
  p.px(8, 10, tier >= 2 ? rim.l2 : R.iron.l1);
  if (tier >= 6) p.ring(8, 6, 4.2, 4, pill.l2, -2.8, -0.3);
  const motes: [number, number][] = [
    [4, 2],
    [12, 2],
    [8, 0],
  ];
  for (let k = 0; k < Math.min(3, Math.floor(tier / 2)); k++) p.px(motes[k]![0], motes[k]![1], pill.l2);
}

registerIcons('good', {
  ...Object.fromEntries(
    Array.from({ length: 7 }, (_, k) => [
      `bt-pill-${k}`,
      { draw: (p: Pix) => pillBox(p, k), draw16: (p: Pix) => pillBox16(p, k) },
    ]),
  ),
  // ยาสลบ: a folded paper packet tied with string, violet powder spilling out.
  'knockout-powder': (p) => {
    p.poly(
      [
        [6, 12],
        [22, 8],
        [26, 22],
        [10, 26],
      ],
      R.paper.b,
    );
    p.poly(
      [
        [6, 12],
        [22, 8],
        [15, 17],
      ],
      R.paper.l1,
    );
    p.line(10, 26, 26, 22, R.paper.d1);
    p.line(8, 18, 24, 14, R.wood.b);
    p.rect(14, 15, 3, 3, C.seal);
    // Spill.
    sprite(p, 20, 23, ['..vv.....', '.vVvvv...', 'vvvvvvvv.', '.v..v..v.'], {
      v: '#8a5ab8',
      V: '#c8a0f0',
    });
    p.px(27, 20, '#c8a0f0').px(29, 24, '#8a5ab8');
  },
  // ธูปสงบจิต: a bronze tripod burner with three incense sticks and a curling thread of smoke.
  'calm-incense': (p) => {
    // Smoke.
    p.path(
      [
        [16, 12],
        [14, 9],
        [17, 6],
        [15, 3],
      ],
      smoke.b,
    );
    p.path(
      [
        [19, 11],
        [21, 7],
        [19, 4],
      ],
      smoke.l1,
    );
    // Sticks.
    for (const [x, t] of [
      [13, 13],
      [16, 12],
      [19, 13],
    ] as const) {
      p.line(x, t, x, 18, R.lacquer.d1);
      p.px(x, t, C.fireLight);
    }
    // Burner: round belly, rim, three legs.
    p.ell(16, 22, 9, 5, R.bronze.d1);
    p.ell(17, 21, 7, 3.5, R.bronze.b);
    p.rect(7, 17, 18, 2, R.bronze.l1);
    p.px(24, 17, R.bronze.l2);
    p.rect(9, 26, 2, 3, R.bronze.d2).rect(15, 27, 2, 3, R.bronze.d2).rect(21, 26, 2, 3, R.bronze.d2);
    p.ring(16, 21, 3, 2, R.bronze.l2, 3.4, 5.8);
  },
  // หมั่นโถว: three steamed buns in a bamboo steamer.
  mantou: (p) => {
    // Steamer.
    p.rect(4, 20, 24, 8, R.wood.l1);
    p.rect(4, 20, 24, 1, R.wood.l2);
    for (let x = 6; x < 28; x += 3) p.line(x, 22, x, 27, R.wood.b);
    p.rect(4, 27, 24, 1, R.wood.d1);
    // Buns: dome clusters, lit top right, cool shadow underside.
    for (const [cx, cy, r] of [
      [10, 18, 5.5],
      [22, 18, 5.5],
      [16, 14, 6],
    ] as const) {
      p.ell(cx, cy, r, r * 0.85, dough.d1);
      p.ell(cx + 0.6, cy - 0.6, r - 1.2, r * 0.85 - 1.2, dough.b);
      p.ell(cx + 1.6, cy - 2, r * 0.35, r * 0.25, dough.l2);
    }
    // Steam.
    for (const x of [9, 16, 23]) p.line(x, 7 - (x % 2), x + 1, 3, C.white);
  },
  // ชาบำรุงจิต: a celadon cup of green tea on a saucer, one leaf, steam.
  'mind-tea': (p) => {
    p.ell(16, 26, 12, 3, porcelain.d1);
    p.ell(16, 25, 11, 2.2, porcelain.b);
    p.poly(
      [
        [7, 14],
        [25, 14],
        [22, 25],
        [10, 25],
      ],
      celadon.b,
    );
    p.poly(
      [
        [7, 14],
        [12, 14],
        [13, 25],
        [10, 25],
      ],
      celadon.d1,
    );
    p.line(22, 15, 21, 23, celadon.l2);
    p.ell(16, 14, 9, 2, '#9ab84a');
    p.ring(16, 14, 9, 2, celadon.l1);
    // Leaf floating.
    sprite(p, 17, 13, ['.gG', 'gg.'], { g: '#4a8a2a', G: '#8ad04a' });
    for (const x of [12, 17, 21]) p.path([[x, 11], [x - 1, 8], [x + 1, 5]], C.white);
  },
});
